// Session configuration and utility functions
const unitToMs = {
    SECOND: 1000,
    MINUTE: 60 * 1000,
    HOUR: 60 * 60 * 1000,
    DAY: 24 * 60 * 60 * 1000,
    WEEK: 7 * 24 * 60 * 60 * 1000,
    FORTNIGHT: 14 * 24 * 60 * 60 * 1000,
    MONTH: 30 * 24 * 60 * 60 * 1000,
    QUARTER: 90 * 24 * 60 * 60 * 1000,
    YEAR: 365 * 24 * 60 * 60 * 1000
}

// Convert session span to milliseconds
export const convertSessionSpanToMs = (span, unit) => {
    const multiplier = unitToMs[unit]
    if (!multiplier) {
        console.warn(`Unsupported session unit: ${unit}. Using MINUTE.`)
        return span * unitToMs.MINUTE
    }
    return span * multiplier
}

// Create session configuration object
export const createSessionConfig = (span, unit) => {
    const calculatedMs = convertSessionSpanToMs(span, unit)
    return {
        span,
        unit,
        calculatedMs,
        refreshThreshold: 0.8 // Refresh at 80% of session time
    }
}

// Extract session configuration from appVariables array
export const extractSessionConfigFromAppVariables = (appVariables) => {
    if (!Array.isArray(appVariables)) {
        return null
    }



    const sessionSpanVar = appVariables.find(item => item.varKey === 'USER.LOGIN.SESSION.SPAN')
    const sessionUnitVar = appVariables.find(item => item.varKey === 'USER.LOGIN.SESSION.SPAN.UNIT')

    if (!sessionSpanVar || !sessionUnitVar) {
        console.warn('Session configuration not found in appVariables')
        return null
    }

    const span = parseInt(sessionSpanVar.varVal)
    const unit = sessionUnitVar.varVal

    if (isNaN(span)) {
        console.warn('Invalid session span value:', sessionSpanVar.varVal)
        return null
    }

    return createSessionConfig(span, unit)
}

// Store session configuration in localStorage
export const storeSessionConfig = (span, unit) => {
    const config = createSessionConfig(span, unit)
    localStorage.setItem('sessionConfig', JSON.stringify(config))
    return config
}

// Store session configuration from appVariables
export const storeSessionConfigFromAppVariables = (appVariables) => {
    const config = extractSessionConfigFromAppVariables(appVariables)
    if (config) {
        localStorage.setItem('sessionConfig', JSON.stringify(config))
        console.log('[SESSION] ⚙️ Session config stored:', config.span, config.unit, '(', Math.floor(config.calculatedMs / 60000), 'minutes )')
        console.log('[SESSION] 📌 Refresh will happen at 80% =', Math.floor((config.calculatedMs * 0.8) / 60000), 'minutes')
        return config
    }
    return null
}

// Get session configuration from localStorage
export const getSessionConfig = () => {
    try {
        const config = localStorage.getItem('sessionConfig')
        return config ? JSON.parse(config) : null
    } catch (error) {
        console.error('Error parsing session config:', error)
        return null
    }
}

// Store token issue time
export const storeTokenIssuedAt = (timestamp = Date.now()) => {
    localStorage.setItem('tokenIssuedAt', timestamp.toString())
    console.log('[SESSION] 🎫 Token issued/updated at:', new Date(timestamp).toLocaleTimeString())
    return timestamp
}

// Get token issue time
export const getTokenIssuedAt = () => {
    const timestamp = localStorage.getItem('tokenIssuedAt')
    return timestamp ? parseInt(timestamp) : null
}

// Store last activity time
export const storeLastActivity = (timestamp = Date.now()) => {
    localStorage.setItem('lastActivity', timestamp.toString())
    return timestamp
}

// Get last activity time
export const getLastActivity = () => {
    const timestamp = localStorage.getItem('lastActivity')
    return timestamp ? parseInt(timestamp) : null
}

// Check if token should be refreshed
export const shouldRefreshToken = () => {
    const now = Date.now()
    const tokenIssuedAt = getTokenIssuedAt()
    const lastActivity = getLastActivity()
    const sessionConfig = getSessionConfig()

    console.log('[SESSION] 📊 shouldRefreshToken - Check Details:')
    console.log('  Current time:', new Date(now).toLocaleTimeString())
    console.log('  Token issued at:', tokenIssuedAt ? new Date(tokenIssuedAt).toLocaleTimeString() : 'NULL')
    console.log('  Last activity:', lastActivity ? new Date(lastActivity).toLocaleTimeString() : 'NULL')
    console.log('  Session config:', sessionConfig ? `${sessionConfig.span} ${sessionConfig.unit}` : 'NULL')

    // Return false if any required data is missing (including session config)
    if (!tokenIssuedAt || !sessionConfig || !lastActivity) {
        console.log('[SESSION] ❌ Missing required data - cannot determine if refresh needed')
        return false
    }

    const tokenAge = now - tokenIssuedAt
    const refreshThreshold = sessionConfig.calculatedMs * sessionConfig.refreshThreshold
    const tokenExpiryTime = tokenIssuedAt + sessionConfig.calculatedMs

    const tokenAgeMinutes = Math.floor(tokenAge / 60000)
    const refreshThresholdMinutes = Math.floor(refreshThreshold / 60000)
    const sessionDurationMinutes = Math.floor(sessionConfig.calculatedMs / 60000)

    console.log('[SESSION] ⏱️ Timing Analysis:')
    console.log('  Token age:', tokenAgeMinutes, 'minutes')
    console.log('  Refresh threshold:', refreshThresholdMinutes, 'minutes (80% of', sessionDurationMinutes, 'min)')
    console.log('  Token expiry time:', new Date(tokenExpiryTime).toLocaleTimeString())
    console.log('[SESSION] 🔍 Condition Checks:')
    console.log('  1. Token age > threshold?', tokenAge > refreshThreshold, `(${tokenAgeMinutes} > ${refreshThresholdMinutes})`)
    console.log('  2. Activity >= token issued?', lastActivity >= tokenIssuedAt, `(${new Date(lastActivity).toLocaleTimeString()} >= ${new Date(tokenIssuedAt).toLocaleTimeString()})`)
    console.log('  3. Activity <= expiry time?', lastActivity <= tokenExpiryTime, `(${new Date(lastActivity).toLocaleTimeString()} <= ${new Date(tokenExpiryTime).toLocaleTimeString()})`)

    // Refresh if:
    // 1. Token age has passed the refresh threshold (80% expired)
    // 2. Last activity occurred between token issue time and token expiry time
    const shouldRefresh = tokenAge > refreshThreshold &&
        lastActivity >= tokenIssuedAt &&
        lastActivity <= tokenExpiryTime

    console.log('[SESSION] 🎯 Final Decision: Should refresh =', shouldRefresh)

    return shouldRefresh
}

// Check if session is completely expired
export const isSessionExpired = () => {
    const now = Date.now()
    const tokenIssuedAt = getTokenIssuedAt()
    const sessionConfig = getSessionConfig()

    console.log('[SESSION] 🔎 isSessionExpired - Check Details:')
    console.log('  Current time:', new Date(now).toLocaleTimeString())
    console.log('  Token issued at:', tokenIssuedAt ? new Date(tokenIssuedAt).toLocaleTimeString() : 'NULL')
    console.log('  Session config:', sessionConfig ? `${sessionConfig.span} ${sessionConfig.unit}` : 'NULL')

    // If no session config available yet, don't consider session expired
    // This prevents premature logout during app initialization
    if (!sessionConfig) {
        console.log('[SESSION] ⚠️ No session config - not considering session expired')
        return false
    }

    // If no token issued time, session is expired
    // if (!tokenIssuedAt) {
    //     return true
    // }

    const tokenAge = now - tokenIssuedAt
    const tokenAgeMinutes = Math.floor(tokenAge / 60000)
    const sessionDurationMinutes = Math.floor(sessionConfig.calculatedMs / 60000)

    const isExpired = tokenAge > sessionConfig.calculatedMs

    console.log('[SESSION] ⏱️ Expiry Check:')
    console.log('  Token age:', tokenAgeMinutes, 'minutes')
    console.log('  Session duration:', sessionDurationMinutes, 'minutes')
    console.log('  Is expired?', isExpired)

    return isExpired
}

// Clear all session data
export const clearSessionData = () => {
    console.log('[SESSION] 🧹 Clearing all session data...')
    localStorage.removeItem('_atk')
    localStorage.removeItem('tokenIssuedAt')
    localStorage.removeItem('lastActivity')
    localStorage.removeItem('sessionConfig')
    localStorage.removeItem('user')
    localStorage.removeItem('tokenRefreshLock') // Clean up refresh lock
}